import React from 'react'
import useMeasure from 'react-use-measure'

import { PSComponent } from '../../models/PSComponent'
import { ReportTotal } from '../../models/ReportTotal'
import { formatNumberForReportMetric } from '../../utils/numbers'

type ReportMetricsViewProps = {
    metrics: PSComponent[]
    totals: { [key: string]: ReportTotal }
    dateType: string
    dateCompareType: string
    activeMeasure: PSComponent
    onSelect: (metric: PSComponent) => void
}

const DeltaSubtitleForMetricsView: React.FC<{
    t: ReportTotal | undefined
    dateCompareType: string
}> = ({ t, dateCompareType }) => {
    const [ref, bounds] = useMeasure()
    const isSuffixNarrow = bounds.width < 120

    let deltaString = t?.deltaString ? t.deltaString.replace('-', '').replace('+', '') : ''
    let text = ''
    let textClass = ''
    let suffix = ''

    if (t?.delta === undefined) {
        text = 'N/A'
        textClass = 'text-gray-500'
    }
    else {
        if (t?.delta && t.delta > 0) {
            text = `↑\u00A0${deltaString}`
            textClass = 'text-[#179b42]'
        }
        else if (t?.delta && t.delta < 0) {
            text = `↓\u00A0${deltaString}`
            textClass = 'text-[#e22216]'
        }
        else {
            text = '0.0%'
            textClass = 'text-gray-500'
        }

        suffix =
            dateCompareType === 'year'
                ? isSuffixNarrow
                    ? 'vs prev. year'
                    : 'vs previous year'
                : dateCompareType === 'period'
                  ? isSuffixNarrow
                      ? 'vs prev. period'
                      : 'vs previous period'
                  : ''
    }

    return (
        <div className="flex w-full max-w-full items-center justify-start gap-x-1">
            <div className={`text-paragraph-sm shrink-0 font-bold ${textClass}`}>{text}</div>
            <div
                className="text-paragraph-sm min-w-0 shrink-1 grow truncate text-left text-gray-600"
                ref={ref}
            >
                {suffix}
            </div>
        </div>
    )
}

export const ReportMetricsPage = ({
    metrics,
    totals,
    dateType,
    dateCompareType,
    activeMeasure,
    onSelect,
}: ReportMetricsViewProps) => {
    return (
        <div className="m-0 grid grid-cols-4 gap-3 p-0">
            {metrics.map((metric, index) => {
                const title = metric.caption || metric.title
                const reportTotal = totals[metric.id!]
                const total = reportTotal
                    ? formatNumberForReportMetric(
                          reportTotal.total,
                          metric.fieldType,
                          reportTotal.totalString
                      )
                    : 'N/A'
                const isSelected = metric.id === activeMeasure.id
                const onClick = () => onSelect(metrics[index])

                return (
                    <button
                        key={metric.id}
                        tabIndex={0}
                        className={`flex cursor-pointer flex-col justify-between rounded-lg p-6 ${
                            isSelected
                                ? 'border-secondary-400 bg-secondary-100 bg-opacity-50 border-2'
                                : 'border border-gray-300 bg-white'
                        } focus-visible:ring-info-400 hover:bg-white hover:bg-gradient-to-b hover:from-black/4 hover:to-black/4 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none active:bg-white active:bg-gradient-to-b active:from-black/8 active:to-black/8`}
                        onClick={onClick}
                    >
                        <div className="text-label-md text-left text-gray-600 capitalize">
                            {title}
                        </div>
                        <div className="text-heading-lg text-left font-bold text-gray-700">
                            {total}
                        </div>
                        {dateType !== 'custom' && (
                            <DeltaSubtitleForMetricsView
                                t={reportTotal}
                                dateCompareType={dateCompareType}
                            />
                        )}
                    </button>
                )
            })}
        </div>
    )
}
